<script setup lang="ts">
import { webSearchTool } from '@/modules/Project/Tools/registry/webSearch'
import { computed, ref } from 'vue'
import DarwinButton from './DarwinButton.vue'
import IconSprite from './IconSprite.vue'
import ToolTip from './ToolTip.vue'

const props = defineProps<{
  value: boolean
}>()
defineEmits<{
  change: [boolean]
}>()

/**
 * We have a bit of hackiness here to make it so that the tooltip doesn't jump
 * to the right when the user enables web search and the button expands. Force the
 * tooltip to be disabled as soon as the mousedown event is triggered, and then
 * re-enable it after a short delay.
 */
const forceDisabled = ref(false)
const onMousedown = () => {
  forceDisabled.value = true
  setTimeout(() => (forceDisabled.value = false), 300)
}

/**
 * Don't show the tooltip if:
 * - Web search is enabled, because the button is already expanded so has visible text
 * - We've forced the tooltip to be disabled because of a mousedown event
 */
const disableTooltip = computed(() => props.value || forceDisabled.value)
</script>

<template>
  <ToolTip
    :disabled="disableTooltip"
    title="Search the web"
    :placement="{ allowedPlacements: ['top'] }"
    :offset="0"
  >
    <DarwinButton
      size="sm"
      rounded
      variant="transparent"
      type="button"
      role="checkbox"
      :aria-checked="value"
      aria-label="Search the web"
      :class="[value && '!bg-background-blue-subtle hover:!bg-background-selected-hovered']"
      @click="$emit('change', !value)"
      @mousedown="onMousedown"
    >
      <template #leading-icon>
        <IconSprite
          :icon="webSearchTool.icon"
          :class="[value ? 'text-text-selected' : 'text-text-subtlest']"
        />
      </template>
      <template
        v-if="value"
        #default
      >
        <div class="text-text-selected">Web search</div>
      </template>
    </DarwinButton>
  </ToolTip>
</template>
